import { useState, useEffect, useCallback } from 'react';
import { getVisibleSections, findNextThreshold } from '../config/visibilityConfig';

/**
 * Result interface for the useVisibleSections hook
 */
export interface UseVisibleSectionsResult {
  visibleSections: string[];
  isSectionVisible: (sectionId: string) => boolean;
}

/**
 * Custom hook that provides reactive visibility state for sections.
 * 
 * This hook:
 * - Returns the current list of visible sections
 * - Provides a memoized function to check if a specific section is visible
 * - Automatically updates when time thresholds are crossed (polls every 60 seconds)
 * - Optimizes by only polling if there are future thresholds to cross
 * - Cleans up interval on unmount to prevent memory leaks
 * 
 * @returns Object containing visibleSections array and isSectionVisible function
 */
export function useVisibleSections(): UseVisibleSectionsResult {
  const [visibleSections, setVisibleSections] = useState<string[]>(() => 
    getVisibleSections()
  );

  useEffect(() => {
    // Calculate next threshold crossing time
    const now = new Date();
    const nextThreshold = findNextThreshold(now);
    
    if (!nextThreshold) {
      // All sections are visible, no need to poll
      return;
    }

    // Set up interval to check visibility every 60 seconds
    const intervalId = setInterval(() => {
      setVisibleSections(getVisibleSections());
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [visibleSections]);

  const isSectionVisible = useCallback(
    (sectionId: string) => visibleSections.includes(sectionId),
    [visibleSections]
  );

  return { visibleSections, isSectionVisible };
}
